<template>
  <Sidebar class="top-16 h-[calc(100vh-4rem)]">
    <SidebarHeader>
      <div class="pt-4 pl-4">
        <SearchButton />
      </div>
    </SidebarHeader>
    <SidebarContent class="pl-4">
      <SidebarGroup>
        <div class="flex items-center justify-between pr-2">
          <SidebarGroupLabel class="uppercase">Projects</SidebarGroupLabel>
          <RouterLink
            :to="{ name: 'project/create' }"
            class="text-muted-foreground hover:text-foreground"
            title="Create project"
          >
            <PlusIcon class="size-4" />
          </RouterLink>
        </div>
        <SidebarMenu v-if="isProjectsLoading">
          <SidebarMenuItem v-for="(width, index) in ['70%', '55%', '80%']" :key="index">
            <div class="flex h-8 w-full items-center gap-2 rounded-md px-2">
              <Skeleton class="size-4.5 shrink-0 rounded-full" />
              <Skeleton class="h-3" :style="{ width }" />
            </div>
          </SidebarMenuItem>
        </SidebarMenu>
        <SidebarMenu v-else>
          <SidebarMenuItem v-for="project in visibleProjects" :key="project.uuid">
            <SidebarMenuButton
              as-child
              class="text-muted-foreground [&.is-active]:bg-primary/5 [&.is-active]:text-primary"
            >
              <RouterLink :to="{ name: 'project', params: { project_uuid: project.uuid } }">
                <span class="size-4.5 shrink-0 overflow-hidden rounded-full">
                  <VProjectIcon
                    width="18"
                    height="18"
                    :name="project.name"
                    :color="project.color"
                    class="text-[10px] font-medium"
                  />
                </span>
                <span class="truncate">{{ project.name }}</span>
              </RouterLink>
            </SidebarMenuButton>
          </SidebarMenuItem>
          <SidebarMenuItem v-if="hasMoreProjects">
            <SidebarMenuButton as-child class="text-muted-foreground">
              <RouterLink :to="{ name: 'projects' }">View all projects</RouterLink>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarGroup>

      <SidebarGroup v-for="group in visibleNavigationGroups" :key="group.label">
        <SidebarGroupLabel class="uppercase">{{ group.label }}</SidebarGroupLabel>
        <SidebarMenu>
          <template v-for="item in group.items" :key="item.name">
            <Collapsible v-if="item.children" class="group/collapsible" :default-open="item.isOpen">
              <SidebarMenuItem>
                <CollapsibleTrigger
                  as-child
                  :class="cn([{ 'bg-sidebar-accent': isChildRouteActive(item.children) }])"
                >
                  <SidebarMenuButton :tooltip="item.tooltip" class="[&_svg]:first:size-4.5">
                    <component :is="item.icon" class="shrink-0" />
                    <span>{{ item.name }}</span>
                    <ChevronRightIcon
                      class="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90"
                    />
                  </SidebarMenuButton>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <SidebarMenuSub>
                    <SidebarMenuSubItem v-for="child in item.children" :key="child.name">
                      <SidebarMenuSubButton
                        as-child
                        class="text-muted-foreground [&.is-active]:bg-primary/5 [&.is-active]:text-primary"
                      >
                        <RouterLink :to="{ name: child.routeName }">
                          {{ child.name }}
                        </RouterLink>
                      </SidebarMenuSubButton>
                    </SidebarMenuSubItem>
                  </SidebarMenuSub>
                </CollapsibleContent>
              </SidebarMenuItem>
            </Collapsible>

            <SidebarMenuItem v-else>
              <SidebarMenuButton
                as-child
                class="text-muted-foreground [&.is-active]:bg-primary/5 [&.is-active]:text-primary [&_svg]:first:size-4.5"
              >
                <RouterLink
                  v-if="item.routeName"
                  :to="{ name: item.routeName, params: item.routeParams }"
                >
                  <component :is="item.icon" class="shrink-0" />
                  <span>{{ item.name }}</span>
                </RouterLink>
                <a
                  v-else-if="item.externalUrl"
                  :href="item.externalUrl"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <component :is="item.icon" class="shrink-0" />
                  <span>{{ item.name }}</span>
                  <ExternalLinkIcon class="ml-auto size-3 opacity-50" />
                </a>
                <span v-else>
                  <component :is="item.icon" class="shrink-0" />
                  <span>{{ item.name }}</span>
                </span>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </template>
        </SidebarMenu>
      </SidebarGroup>
    </SidebarContent>
    <SidebarFooter class="items-center">
      <UnlockFeatures v-if="IS_BILLING_ENABLED ? !isVerified && isMember : false" />
      <ThemeSwitcher />
    </SidebarFooter>
  </Sidebar>
</template>

<script setup lang="ts">
import { ChevronRightIcon, ExternalLinkIcon, PlusIcon } from '@lucide/vue';
import { useQuery } from '@tanstack/vue-query';
import { type Component, computed, watch } from 'vue';
import { useRoute } from 'vue-router';

import {
  type Profile,
  profileQueryOptions,
  useProfileStatus,
  type UserFeature,
} from '@/entities/profile';
import { projectsQueryOptions, VProjectIcon } from '@/entities/project';
import { teamQueryOptions } from '@/entities/team';
import { IS_BAREMETAL_ENABLED, IS_BILLING_ENABLED, SUPPORT_URL } from '@/shared/config';
import { useTeamCookie } from '@/shared/hooks/use-team-cookie';
import { cn } from '@/shared/lib/misc';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/shared/ui/collapsible';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from '@/shared/ui/sidebar';
import { Skeleton } from '@/shared/ui/skeleton';
import ThemeSwitcher from '@/shared/ui/theme-switcher.vue';

import { SearchButton } from '../search-overlay';
import ApiIcon from './icons/Api.vue';
import BillingIcon from './icons/Billing.vue';
import BucketIcon from './icons/Buckets.vue';
import IaasIcon from './icons/IaaS.vue';
import ImagesIcon from './icons/Images.vue';
import NetworkIcon from './icons/Network.vue';
import SecurityIcon from './icons/Security.vue';
import StackletIcon from './icons/Stacklets.vue';
import SupportIcon from './icons/Support.vue';
import TeamIcon from './icons/Teams.vue';
import UserscriptsIcon from './icons/Userscripts.vue';
import UnlockFeatures from './unlock-features.vue';

const VISIBLE_PROJECTS = 8;

const route = useRoute();
const { data: profile } = useQuery(profileQueryOptions());
const { data: projects, isLoading: isProjectsLoading } = useQuery(projectsQueryOptions());
const { isVerified, isMember } = useProfileStatus(profile);
const { setOpenMobile } = useSidebar();
const { currentTeamUuid } = useTeamCookie();
const { data: team } = useQuery(teamQueryOptions(currentTeamUuid));

const visibleProjects = computed(() => projects.value?.slice(0, VISIBLE_PROJECTS) ?? []);
const hasMoreProjects = computed(() => (projects.value?.length ?? 0) > VISIBLE_PROJECTS);

interface NavigationItem {
  name: string;
  icon: Component;
  tooltip?: string;
  routeName?: string;
  routeParams?: Record<string, string>;
  externalUrl?: string;
  defaultOpen?: boolean;
  isVisible?: (profile: Profile | undefined) => boolean;
  children?: {
    name: string;
    routeName: string;
    isVisible?: (profile: Profile | undefined) => boolean;
  }[];
}

interface NavigationGroup {
  label: string;
  items: NavigationItem[];
}

const navigationGroups = computed((): NavigationGroup[] => [
  {
    label: 'Platform',
    items: [
      {
        name: 'Compute',
        icon: StackletIcon,
        tooltip: 'Compute',
        defaultOpen: true,
        children: [
          {
            name: 'Stacklets',
            routeName: 'stacklets',
          },
          {
            name: 'Baremetal',
            routeName: 'baremetal',
            isVisible: (profile: Profile | undefined) =>
              IS_BAREMETAL_ENABLED && (profile?.features?.includes('baremetal') ?? false),
          },
        ],
      },
      {
        name: 'Images',
        icon: ImagesIcon,
        tooltip: 'Images',
        children: [
          {
            name: 'Snapshots',
            routeName: 'images/snapshots',
          },
          {
            name: 'Backups',
            routeName: 'images/backups',
          },
          {
            name: 'Custom Images',
            routeName: 'images/custom',
            isVisible: (profile: Profile | undefined) =>
              profile?.features?.includes('custom_images') ?? false,
          },
        ],
      },
      {
        name: 'Networking',
        icon: NetworkIcon,
        tooltip: 'Networking',
        isVisible: (profile: Profile | undefined) =>
          (['floating_ips', 'firewalls', 'domains', 'load_balancers', 'vpc'] as UserFeature[]).some(
            (feature) => profile?.features?.includes(feature),
          ),
        children: [
          {
            name: 'Floating IPs',
            routeName: 'networking/floating-ips',
            isVisible: (profile: Profile | undefined) =>
              profile?.features?.includes('floating_ips') ?? false,
          },
          {
            name: 'Domains',
            routeName: 'networking/domains',
            isVisible: (profile: Profile | undefined) =>
              profile?.features?.includes('domains') ?? false,
          },
          {
            name: 'Firewalls',
            routeName: 'networking/firewalls',
            isVisible: (profile: Profile | undefined) =>
              profile?.features?.includes('firewalls') ?? false,
          },
          {
            name: 'Load Balancers',
            routeName: 'networking/loadbalancers',
            isVisible: (profile: Profile | undefined) =>
              profile?.features?.includes('load_balancers') ?? false,
          },
          {
            name: 'VPC',
            routeName: 'networking/vpc',
            isVisible: (profile: Profile | undefined) =>
              profile?.features?.includes('vpc') ?? false,
          },
        ],
      },
      {
        name: 'Storages',
        icon: BucketIcon,
        tooltip: 'Storages',
        isVisible: (profile: Profile | undefined) =>
          (profile?.features?.includes('volumes') || profile?.features?.includes('buckets')) ??
          false,
        children: [
          {
            name: 'Buckets',
            routeName: 'buckets',
            isVisible: (profile: Profile | undefined) =>
              profile?.features?.includes('buckets') ?? false,
          },
          {
            name: 'Volumes',
            routeName: 'volumes',
            isVisible: (profile: Profile | undefined) =>
              profile?.features?.includes('volumes') ?? false,
          },
        ],
      },
      {
        name: 'IaaS',
        icon: IaasIcon,
        tooltip: 'IaaS',
        isVisible: (profile: Profile | undefined) =>
          (profile?.features?.includes('container_registry') ||
            profile?.features?.includes('databases') ||
            profile?.features?.includes('kubernetes')) ??
          false,
        children: [
          {
            name: 'Databases',
            routeName: 'databases',
            isVisible: (profile: Profile | undefined) =>
              profile?.features?.includes('databases') ?? false,
          },
          {
            name: 'Kubernetes',
            routeName: 'kubernetes',
            isVisible: (profile: Profile | undefined) =>
              profile?.features?.includes('kubernetes') ?? false,
          },
          {
            name: 'Container Registry',
            routeName: 'registry',
            isVisible: (profile: Profile | undefined) =>
              profile?.features?.includes('container_registry') ?? false,
          },
        ],
      },
    ],
  },
  {
    label: team.value ? `${team.value.name} workspace` : 'Personal workspace',
    items: [
      {
        name: 'Members',
        icon: TeamIcon,
        routeName: 'team',
        routeParams: { uuid: currentTeamUuid.value },
        isVisible: () => !!currentTeamUuid.value,
      },
      {
        name: 'Billing',
        icon: BillingIcon,
        routeName: 'billing',
        isVisible: () => IS_BILLING_ENABLED,
      },
      {
        name: 'Security',
        icon: SecurityIcon,
        routeName: 'security',
      },
      {
        name: 'Userscripts',
        icon: UserscriptsIcon,
        routeName: 'userscripts',
      },
      {
        name: 'API',
        icon: ApiIcon,
        routeName: 'api',
      },
      {
        name: 'Support',
        icon: SupportIcon,
        externalUrl: SUPPORT_URL,
        isVisible: () => !!SUPPORT_URL,
      },
    ],
  },
]);

const isChildRouteActive = (children?: NavigationItem['children']) => {
  if (!children) return false;
  return children.some((child) => route.name === child.routeName);
};

const visibleNavigationGroups = computed(() =>
  navigationGroups.value.map((group) => ({
    ...group,
    items: group.items
      .filter((item) => !item.isVisible || item.isVisible(profile.value))
      .map((item) => ({
        ...item,
        isOpen: item.defaultOpen || isChildRouteActive(item.children),
        children: item.children?.filter(
          (child) => !child.isVisible || child.isVisible(profile.value),
        ),
      }))
      .filter((item) => !item.children || item.children.length > 0),
  })),
);

watch(route, () => setOpenMobile(false));
</script>
